// #region Imports
import type { ReactElement, ComponentPropsWithoutRef } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Button, IconButton, Tooltip, Spinner } from '@radix-ui/themes';
import { Provider as TooltipProvider } from '@radix-ui/react-tooltip';
import type { AZButtonProps } from './AZButton.types';
import './AZButton.css';
// #endregion

// #region CVA Variants (kept for type export + VariantProps compatibility)
export const buttonVariants = cva('', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
      ghost: '',
      danger: '',
      icon: '',
    },
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});
// #endregion

// #region Internal maps
const RADIX_SIZE = { sm: '1', md: '2', lg: '3' } as const;
const RADIX_VARIANT = {
  primary: 'solid',
  secondary: 'outline',
  ghost: 'ghost',
  danger: 'solid',
  icon: 'solid',
} as const;
// #endregion

// #region Component
/**
 * Versatile button with optional tooltip and icon support.
 *
 * @example
 * <AZButton description="Save" icon={<FaSave />} onClick={handleSave} />
 * <AZButton variant="danger" size="sm" onClick={handleDelete}>Delete</AZButton>
 * <AZButton variant="icon" description="Copy" icon={<FaCopy />} />
 */
const AZButton = ({
  variant = 'secondary',
  size = 'lg',
  description,
  icon,
  tooltipSide = 'top',
  isLoading = false,
  className,
  disabled,
  children,
  expendable = false,
  color = 'amber',
  ...rest
}: AZButtonProps) => {
  const resolvedVariant = variant ?? 'secondary';
  const resolvedSize = size ?? 'lg';
  const radixSize = RADIX_SIZE[resolvedSize];
  const isIconVariant = resolvedVariant === 'icon';
  const isPrimary = resolvedVariant === 'primary' || resolvedVariant === 'icon';
  const isDanger = resolvedVariant === 'danger';
  const isGhost = resolvedVariant === 'ghost';
  const sharedDataAttrs = {
    'data-az-variant': resolvedVariant,
    'data-az-size': resolvedSize,
  };

  let buttonEl: ReactElement;

  if (isIconVariant) {
    buttonEl = (
      <IconButton
        size={radixSize}
        variant="outline"
        radius="full"
        color={isDanger ? 'red' : color}
        disabled={disabled || isLoading}
        aria-label={description}
        aria-busy={isLoading || undefined}
        className={clsx(
          'az-primary-scope',
          expendable && 'az-btn-expandable',
          !disabled && !isLoading && 'az-btn-hoverable-cursor',
          !isLoading && !disabled && !isDanger && 'az-btn-hoverable',
          (disabled || isLoading) && 'az-btn-disabled',
          className,
        )}
        {...sharedDataAttrs}
        {...(rest as Omit<ComponentPropsWithoutRef<'button'>, 'color'>)}
      >
        {isLoading ? <Spinner /> : icon}
        {icon && expendable && description && (
          <span className="az-btn-expandable-label">{description}</span>
        )}
      </IconButton>
    );
  } else {
    buttonEl = (
      <Button
        size={radixSize}
        variant={RADIX_VARIANT[resolvedVariant]}
        color={isDanger ? 'red' : color}
        disabled={disabled || isLoading}
        aria-label={!children ? description : undefined}
        aria-busy={isLoading || undefined}
        className={clsx(
          isPrimary && !isDanger && 'az-primary-scope',
          expendable && 'az-btn-expandable',
          !disabled && !isLoading && 'az-btn-hoverable-cursor',
          !isLoading && !disabled && !isDanger && 'az-btn-hoverable',
          (disabled || isLoading) && 'az-btn-disabled',
          isGhost && 'az-btn-ghost',
          className,
        )}
        {...sharedDataAttrs}
        {...(rest as Omit<ComponentPropsWithoutRef<'button'>, 'color'>)}
      >
        {isLoading ? <Spinner /> : icon}
        {children}
        {icon && expendable && description && (
          <span className="az-btn-expandable-label">{description}</span>
        )}
      </Button>
    );
  }

  // #region Interface
  if (!description || expendable) return buttonEl;

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip content={description} side={tooltipSide}>
        {buttonEl}
      </Tooltip>
    </TooltipProvider>
  );
  // #endregion
};
// #endregion

// #region Display Name
AZButton.displayName = 'AZButton';
// #endregion

export { AZButton };
